import { truthy } from './types'
import { formatDateTime } from './format'

export type StatusKind = 'open' | 'closingToday' | 'soon' | 'full' | 'closed' | 'entered'
export type CtaKind = 'primary' | 'secondary' | 'dark' | 'danger'

export interface RegistrationStatus {
  kind: StatusKind
  /** "Open · closes Oct 9, 3:00 PM ET" / "Closes today · 5:00 PM ET" / "Opens Oct 10, 8:00 PM ET" /
   *  "Full · 220 / 220" / "You're entered · 3 classes" / "Closed" */
  label: string
  dotColor: string
  textColor: string
  cta: string
  ctaKind: CtaKind
  /** True only for kind 'entered' when the racer's own entry isn't paid yet. `label` stays plain
   *  (green, same as every other entered state) - callers render this as a separate red "Unpaid"
   *  suffix instead of baking a second color into one plain-text label. */
  unpaid: boolean
  /** Overrides the card's own displayEvent.cfm href for this one CTA - only set for the unpaid
   *  "Pay" CTA, which needs to go straight to checkout rather than the event page. Every other
   *  CTA leaves this unset and falls back to the href the card passes CtaButton. */
  ctaHref?: string
}

interface StatusInput {
  id: number
  signupWindowOpen: unknown
  full: unknown
  openPaymentDT: string
  closePaymentDT: string
  enteredClassCount: number
  enteredPaid: unknown
}

const COLOR = {
  open: { dot: '#1f9d4c', text: '#1f6f36' },
  closing: { dot: '#e35d00', text: '#a84700' },
  soon: { dot: '#2f6fdb', text: '#1d4f9e' },
  full: { dot: '#8a8e95', text: '#5b5f66' },
}

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString()
}

interface LimitCountInput {
  entryLimit: number | string
  entryCount: number | string
  racerLimit: number | string
  racerCount: number | string
}

/** Resolves the limit/count pair an event is actually gated by - racerLimit/racerCount when the
 *  track capped by total racers, else entryLimit/entryCount. Same racerLimit-takes-priority
 *  convention as eventsList.cfc's own full-gate and event-page's RegistrationCard.tsx
 *  (usingRacerLimit). Shared by entryMeta() (EventListCard.tsx), EventRow's progress-bar fill,
 *  and PromoHero's thermometer so the convention is only stated once - checking entryLimit/
 *  entryCount alone both undercounts a racerLimit-capped event's limit (entryLimit then reads as
 *  whatever per-class sum the backend computed, or 0 - which also silently hides the progress
 *  bar entirely, since its own `limit > 0` guard never passes) and overcounts its "entered" total
 *  (entryCount is total class-entries, which runs higher than the racer count once racers enter
 *  more than one class each). */
export function entryLimitAndCount(event: LimitCountInput): { limit: number; count: number } {
  const usingRacerLimit = event.racerLimit !== '' && event.racerLimit !== null && event.racerLimit !== undefined
  return {
    limit: Number(usingRacerLimit ? event.racerLimit : event.entryLimit) || 0,
    count: Number(usingRacerLimit ? event.racerCount : event.entryCount) || 0,
  }
}

/** Pure, UI-agnostic derivation shared by every card/row shape (mobile list, desktop list,
 *  flyer cards, Featured/RaceTime heroes). Mirrors webshared/controllers/eventPage.cfc's
 *  checkEventGate's own open/full formula (signupWindowOpen already computed server-side the
 *  same way: now between openPaymentDT/closePaymentDT) - this function only adds the UI-facing
 *  label/CTA layer on top of that same source of truth, it doesn't re-derive open/full itself. */
export function deriveStatus(e: StatusInput): RegistrationStatus {
  if (e.enteredClassCount > 0) {
    const paid = truthy(e.enteredPaid)
    return {
      kind: 'entered',
      label: `You're entered · ${e.enteredClassCount} class${e.enteredClassCount === 1 ? '' : 'es'}${paid ? ' · paid' : ''}`,
      dotColor: COLOR.open.dot,
      textColor: COLOR.open.text,
      // Paid: "My entry" to the event page, same as every other CTA. Unpaid: "Pay" straight to
      // checkout (racers/checkout.cfm?eid=, the same URL eventPage.cfc's own checkoutUrl builds)
      // - checkout.cfm only lists unpaid entries, so sending a paid racer there would show "No
      // Entries" instead of anything useful. "Pay" gets its own red (danger) treatment - same red
      // as the "Unpaid" label - so it reads as the action the Unpaid label is calling out, rather
      // than looking like "My entry"'s black or Sign up's orange.
      cta: paid ? 'My entry' : 'Pay',
      ctaKind: paid ? 'dark' : 'danger',
      unpaid: !paid,
      ctaHref: paid ? undefined : `/racers/checkout.cfm?eid=${e.id}`,
    }
  }

  if (truthy(e.full)) {
    return {
      kind: 'full',
      label: 'Full',
      dotColor: COLOR.full.dot,
      textColor: COLOR.full.text,
      cta: 'Details',
      ctaKind: 'secondary',
      unpaid: false,
    }
  }

  const now = new Date()
  const opens = e.openPaymentDT ? new Date(e.openPaymentDT) : null
  const closes = e.closePaymentDT ? new Date(e.closePaymentDT) : null

  if (!truthy(e.signupWindowOpen)) {
    if (opens && !Number.isNaN(opens.getTime()) && now < opens) {
      return {
        kind: 'soon',
        label: `Opens ${formatDateTime(e.openPaymentDT)}`,
        dotColor: COLOR.soon.dot,
        textColor: COLOR.soon.text,
        cta: 'Remind me',
        ctaKind: 'secondary',
        unpaid: false,
      }
    }
    // Either closePaymentDT already passed, or no signup window was ever configured for this
    // event - both read the same to a viewer: there's nothing to sign up for right now.
    return {
      kind: 'closed',
      label: 'Closed',
      dotColor: COLOR.full.dot,
      textColor: COLOR.full.text,
      cta: 'Details',
      ctaKind: 'secondary',
      unpaid: false,
    }
  }

  if (closes && !Number.isNaN(closes.getTime()) && isSameDay(now, closes)) {
    return {
      kind: 'closingToday',
      label: `Closes today · ${formatDateTime(e.closePaymentDT).split(', ').pop() || ''}`,
      dotColor: COLOR.closing.dot,
      textColor: COLOR.closing.text,
      cta: 'Sign up',
      ctaKind: 'primary',
      unpaid: false,
    }
  }

  return {
    kind: 'open',
    label: closes ? `Open · closes ${formatDateTime(e.closePaymentDT)}` : 'Open',
    dotColor: COLOR.open.dot,
    textColor: COLOR.open.text,
    cta: 'Sign up',
    ctaKind: 'primary',
    unpaid: false,
  }
}
